import { useEffect, useCallback } from 'react'

interface ShortcutHandlers {
  onSave?: () => void
  onToggleHelp?: () => void
  onStartTour?: () => void
  onClose?: () => void
  isModalOpen: boolean
}

/**
 * Keyboard shortcuts hook for the settings modal
 *
 * Shortcuts (scoped to modal visibility):
 * - Cmd/Ctrl + S: Save settings
 * - Cmd/Ctrl + H: Toggle help sidebar
 * - Cmd/Ctrl + ?: Start guided tour
 * - Escape: Close modal
 *
 * Screen reader announcements are handled via aria-live regions in the UI
 */
export function useKeyboardShortcuts({
  onSave,
  onToggleHelp,
  onStartTour,
  onClose,
  isModalOpen
}: ShortcutHandlers) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    // Only handle shortcuts when modal is open
    if (!isModalOpen) return

    // Accept either metaKey (Mac) or ctrlKey (Windows/Linux)
    const modifier = e.metaKey || e.ctrlKey

    // Cmd/Ctrl + S - Save
    if (modifier && e.key.toLowerCase() === 's') {
      e.preventDefault()
      onSave?.()
      return
    }

    // Cmd/Ctrl + H - Toggle Help
    if (modifier && e.key.toLowerCase() === 'h') {
      e.preventDefault()
      onToggleHelp?.()
      return
    }

    // Cmd/Ctrl + ? - Start Tour (shift + / = ?)
    if (modifier && e.shiftKey && e.key === '?') {
      e.preventDefault()
      onStartTour?.()
      return
    }

    // Escape - Close Modal
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose?.()
      return
    }
  }, [isModalOpen, onSave, onToggleHelp, onStartTour, onClose])

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}

export default useKeyboardShortcuts
